import {
    TrackLoanBuilderError,
    TrackOfferSelected
} from '../../../../tracking/amplitude.helpers';
import {
    APPROVAL_EXPIRED_STATUS,
    APPROVED_STATUS
} from '../../../multiLoanDashboard/accountsSelector';
import {
    MY_OFFERS_LOADING,
    MY_OFFERS_READY,
    MY_OFFERS_INPUT_ERROR,
    MY_OFFERS_REPAYMENT_SCHEDULE_MODAL_DATA,
    MY_OFFERS_DRAWDOWN_SCHEDULES_READY,
    MY_OFFERS_DRAWDOWN_SCHEDULES_LOADING,
    SHOW_EARLY_REPAYMENT_BREAKDOWN,
    SET_SELECTED_OFFER,
    SET_DRAWDOWN_AMOUNT
} from '../../MyOffers.actions';
import {
    getAvailableCash,
    getScheduleForModal,
    getTimeOutDate,
    normaliseEarlyRepaymentSchedule
} from '../../MyOffers.helpers';
import {
    Error,
    FormalOffers,
    NormalisedRepaymentSchedule,
    DrawdownRepaymentSchedule
} from '../../MyOffers.types';

const initialState = {
    ready: false,
    offerStatus: null,
    maxCashAmount: 0,
    timeoutDate: '',
    minDrawdownAmount: 0,
    errors: [],
    formalOffers: null,
    drawdownSchedules: null,
    drawdownScheduleLoading: false,
    modalSchedule: null,
    earlyRepaymentSchedule: null,
    earlyRepaymentReady: false,
    selectedOfferId: null,
    drawdownAmount: 0
};

type LoanBuilderState = {
    ready: boolean;
    offerStatus: typeof APPROVAL_EXPIRED_STATUS | typeof APPROVED_STATUS | null;
    maxCashAmount: number;
    timeoutDate: string;
    minDrawdownAmount: number;
    errors: Error[];
    formalOffers: FormalOffers | null;
    drawdownSchedules: NormalisedRepaymentSchedule[] | null;
    modalSchedule: NormalisedRepaymentSchedule | null;
    drawdownScheduleLoading: boolean;
    earlyRepaymentSchedule: NormalisedRepaymentSchedule | null;
    earlyRepaymentReady: boolean;
    selectedOfferId: string | null;
    drawdownAmount: number;
};

type LoanBuilderReadyAction = {
    type: typeof MY_OFFERS_READY;
    formalOffers: FormalOffers;
    drawdownSchedules: NormalisedRepaymentSchedule[];
};

type LoanBuilderDrawdownScheduleReady = {
    type: typeof MY_OFFERS_DRAWDOWN_SCHEDULES_READY;
    newDrawdownSchedules: NormalisedRepaymentSchedule[];
};

type LoanBuilderEarlyRepaymentScheduleAction = {
    type: typeof SHOW_EARLY_REPAYMENT_BREAKDOWN;
    earlyRepaymentSchedule: DrawdownRepaymentSchedule;
};

type LoanBuilderErrorAction = {
    type: typeof MY_OFFERS_INPUT_ERROR;
    error: Error;
};

type LoanBuilderSetSelectedOfferAction = {
    type: typeof SET_SELECTED_OFFER;
    selectedOfferId: string;
};

type LoanBuilderSetDrawdownAmountAction = {
    type: typeof SET_DRAWDOWN_AMOUNT;
    drawdownAmount: number;
};

type LoanBuilderRepaymentScheduleModalAction = {
    type: typeof MY_OFFERS_REPAYMENT_SCHEDULE_MODAL_DATA;
    isEarlyRepaymentSchedule: boolean;
    offerId: string;
};

type LoanBuilderAction =
    | LoanBuilderReadyAction
    | { type: typeof MY_OFFERS_LOADING }
    | LoanBuilderErrorAction
    | LoanBuilderRepaymentScheduleModalAction
    | LoanBuilderDrawdownScheduleReady
    | { type: typeof MY_OFFERS_DRAWDOWN_SCHEDULES_LOADING }
    | LoanBuilderEarlyRepaymentScheduleAction
    | LoanBuilderSetSelectedOfferAction
    | LoanBuilderSetDrawdownAmountAction;

// eslint-disable-next-line import/no-default-export
export default function (
    state: LoanBuilderState = initialState,
    action: LoanBuilderAction
) {
    switch (action.type) {
        case MY_OFFERS_READY:
            const { formalOffers, drawdownSchedules } = action;
            return {
                ...state,
                ready: true,
                maxCashAmount: getAvailableCash(formalOffers!),
                timeoutDate: getTimeOutDate(formalOffers!),
                formalOffers: formalOffers,
                drawdownSchedules: drawdownSchedules,
                drawdownAmount: getAvailableCash(formalOffers!), //on first load the drawdownAmount is defaulted to the available_cash
                minDrawdownAmount: 150
            };
        case MY_OFFERS_DRAWDOWN_SCHEDULES_LOADING:
            return {
                ...state,
                drawdownScheduleLoading: true
            };
        case MY_OFFERS_DRAWDOWN_SCHEDULES_READY:
            const { newDrawdownSchedules } = action;
            return {
                ...state,
                drawdownScheduleLoading: false,
                drawdownSchedules: newDrawdownSchedules
            };
        case SET_SELECTED_OFFER:
            const { selectedOfferId } = action;
            const selectedOffer = state.formalOffers!.find(
                (offer) => offer.offer_id === selectedOfferId
            );
            TrackOfferSelected({
                'Product Type': selectedOffer!.product_type!,
                'Offer duration': selectedOffer!.duration!.amount
            });
            return {
                ...state,
                selectedOfferId: selectedOfferId,
                earlyRepaymentReady: false,
                earlyRepaymentSchedule: null,
                errors: []
            };
        case SET_DRAWDOWN_AMOUNT:
            const { drawdownAmount } = action;
            return {
                ...state,
                drawdownAmount: drawdownAmount,
                earlyRepaymentReady: false,
                earlyRepaymentSchedule: null,
                selectedOfferId: null
            };
        case SHOW_EARLY_REPAYMENT_BREAKDOWN:
            const { earlyRepaymentSchedule } = action;
            return {
                ...state,
                earlyRepaymentSchedule: normaliseEarlyRepaymentSchedule(
                    earlyRepaymentSchedule,
                    state.selectedOfferId!
                ),
                earlyRepaymentReady: true
            };
        case MY_OFFERS_LOADING:
            return {
                ...state,
                ready: false
            };
        case MY_OFFERS_INPUT_ERROR:
            const { error } = action;
            const newError: Error = {
                source: error.source,
                type: error.type
            };
            TrackLoanBuilderError({
                source: newError.source!,
                type: newError.type!
            });
            return {
                ...state,
                errors: [...state.errors, newError]
            };
        case MY_OFFERS_REPAYMENT_SCHEDULE_MODAL_DATA:
            const { isEarlyRepaymentSchedule, offerId } = action;

            if (isEarlyRepaymentSchedule) {
                return {
                    ...state,
                    modalSchedule: state.earlyRepaymentSchedule
                };
            } else
                return {
                    ...state,
                    modalSchedule: getScheduleForModal(
                        offerId!,
                        state.drawdownSchedules!
                    )
                };

        default:
            return state;
    }
}
